import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { History, Search, RefreshCw } from "lucide-react";
import { formatDate } from "@/lib/format";
import Pagination, { usePagination } from "@/components/Pagination";

const RESOURCE_TONES = {
  invoice: "bg-blue-50 text-blue-700",
  payment: "bg-emerald-50 text-emerald-700",
  retention: "bg-amber-50 text-amber-700",
  voucher: "bg-indigo-50 text-indigo-700",
  role: "bg-fuchsia-50 text-fuchsia-700",
  user: "bg-violet-50 text-violet-700",
  project: "bg-cyan-50 text-cyan-700",
  party: "bg-teal-50 text-teal-700",
  session: "bg-slate-100 text-slate-700",
  status_option: "bg-rose-50 text-rose-700",
};

const ACTION_TONES = {
  create: "bg-emerald-50 text-emerald-700 border-emerald-200",
  update: "bg-amber-50 text-amber-700 border-amber-200",
  delete: "bg-rose-50 text-rose-700 border-rose-200",
  status: "bg-indigo-50 text-indigo-700 border-indigo-200",
  login: "bg-slate-100 text-slate-700 border-slate-200",
  pay: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const RESOURCE_OPTIONS = ["invoice", "payment", "retention", "voucher", "role", "user", "project", "party", "status_option", "session"];
const ACTION_OPTIONS = ["create", "update", "delete", "status", "login", "pay"];

function tsLocal(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return s;
  }
}

export default function AuditLog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [resource, setResource] = useState("all");
  const [action, setAction] = useState("all");

  const load = async () => {
    try {
      setLoading(true);
      const params = { limit: 500 };
      if (q.trim()) params.q = q.trim();
      if (resource !== "all") params.resource_type = resource;
      if (action !== "all") params.action = action;
      const { data } = await api.get("/audit-logs", { params });
      setRows(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [resource, action]);

  const {
    pageItems: pagedRows,
    page: aPage, setPage: setAPage,
    pageSize: aPageSize, setPageSize: setAPageSize,
    totalPages: aTotalPages, totalItems: aTotalItems,
  } = usePagination(rows, "voltledger.audit.pageSize");

  const totals = useMemo(() => {
    const t = { byAction: {}, byResource: {} };
    rows.forEach((r) => {
      t.byAction[r.action] = (t.byAction[r.action] || 0) + 1;
      t.byResource[r.resource_type] = (t.byResource[r.resource_type] || 0) + 1;
    });
    return t;
  }, [rows]);

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1500px]">
      <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-red-600/90">Administration</div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-1">Audit Log</h1>
          <p className="text-sm text-slate-500 mt-2">
            System-wide trail of who changed what. Most recent first. Latest 500 entries are shown.
          </p>
        </div>
        <Button onClick={load} variant="outline" className="rounded-none h-10" data-testid="audit-refresh">
          <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="bg-white border border-slate-200 p-4 mb-6 flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[260px]">
          <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Search</Label>
          <div className="relative mt-1">
            <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
            <Input
              placeholder="User, email, summary, resource id…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              className="rounded-none pl-9"
              data-testid="audit-search"
            />
          </div>
        </div>
        <div className="w-48">
          <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Resource</Label>
          <Select value={resource} onValueChange={setResource}>
            <SelectTrigger className="rounded-none mt-1" data-testid="audit-resource"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All resources</SelectItem>
              {RESOURCE_OPTIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-40">
          <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Action</Label>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="rounded-none mt-1" data-testid="audit-action"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              {ACTION_OPTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={load} variant="outline" className="rounded-none h-10" data-testid="audit-apply">Apply</Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4 text-[11px]">
        <span className="chip-soft info">Showing {rows.length} entries</span>
        {Object.entries(totals.byAction).map(([a, n]) => (
          <span key={a} className={`px-2 py-0.5 rounded border ${ACTION_TONES[a] || "bg-slate-50 text-slate-700 border-slate-200"}`}>
            {a} · {n}
          </span>
        ))}
      </div>

      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">When</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">User</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Action</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Resource</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Summary</th>
              <th className="text-left px-4 py-3 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Resource ID</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="text-center text-sm text-slate-400 py-16">
                <History className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                {loading ? "Loading…" : "No audit entries match your filters."}
              </td></tr>
            )}
            {pagedRows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50/70" data-testid={`audit-row-${r.id}`}>
                <td className="px-4 py-3 text-xs text-slate-700 whitespace-nowrap">{tsLocal(r.ts)}</td>
                <td className="px-4 py-3 text-slate-700">
                  <div className="font-medium text-slate-900 text-sm">{r.user_name || "—"}</div>
                  <div className="text-[10px] text-slate-400">{r.user_email || ""}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded border text-[10px] font-semibold uppercase tracking-wider ${ACTION_TONES[r.action] || "bg-slate-100 text-slate-700 border-slate-200"}`}>
                    {r.action}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${RESOURCE_TONES[r.resource_type] || "bg-slate-100 text-slate-700"}`}>
                    {r.resource_type}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-700">{r.summary || "—"}</td>
                <td className="px-4 py-3 font-mono-data text-[11px] text-slate-500 truncate max-w-[180px]">{r.resource_id || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pagination
          page={aPage} setPage={setAPage}
          pageSize={aPageSize} setPageSize={setAPageSize}
          totalPages={aTotalPages} totalItems={aTotalItems}
          testId="audit-pagination"
        />
      </div>
    </div>
  );
}
